/**
 * PAUSED ENGINES: the (fetcher, engine) pairs the search breaker is skipping
 * after repeated failures (`networking/serp_breaker.py`), each with the
 * searches that tripped it and a button to turn it back on now instead of
 * waiting out the six hours. Room 50's dig parked Yahoo and DuckDuckGo on the
 * only browser there was, and the only way back was a Python one-liner.
 */
import { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import type { BreakerEvidence, EngineMatrix } from "../../types";

const FETCHER_LABELS: Record<string, string> = {
  Http: "Plain HTTP",
  Crawl4ai: "crawl4ai",
  BrowserPluginV2: "Connected extension",
  Serper: "Serper",
  BrowserPlugin: "Queued extension",
};

export interface PausedPair {
  fetcher: string;
  engine: string;
  secondsLeft: number;
  why: BreakerEvidence[];
}

/** Every paused pair, once each. Serper answers from Google whatever is asked,
 *  so its one pause shows on every engine's row and is listed once, as Google.
 *  Pure. */
export function pausedPairs(matrix: EngineMatrix | null): PausedPair[] {
  const out = new Map<string, PausedPair>();
  for (const row of matrix?.rows ?? []) {
    for (const [fetcher, cell] of Object.entries(row.fetchers)) {
      if (!cell.skipped_for_s) continue;
      const engine = fetcher === "Serper" ? "Google" : row.engine;
      const key = `${fetcher}:${engine}`;
      if (!out.has(key)) {
        out.set(key, {
          fetcher,
          engine,
          secondsLeft: cell.skipped_for_s,
          why: cell.skipped_because ?? [],
        });
      }
    }
  }
  return Array.from(out.values()).sort((a, b) => b.secondsLeft - a.secondsLeft);
}

/** "about 5 hours", "about 40 minutes". Pure. */
export function timeLeft(seconds: number): string {
  if (seconds >= 3600) {
    const h = Math.round(seconds / 3600);
    return `about ${h} hour${h === 1 ? "" : "s"}`;
  }
  const m = Math.max(1, Math.round(seconds / 60));
  return `about ${m} minute${m === 1 ? "" : "s"}`;
}

export default function PausedEngines({ onChange }: { onChange?: () => void }) {
  const [matrix, setMatrix] = useState<EngineMatrix | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setMatrix(await api.getEngineMatrix());
    } catch {
      setMatrix(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const turnOn = async (p: PausedPair) => {
    const key = `${p.fetcher}:${p.engine}`;
    setBusy(key);
    setError("");
    try {
      await api.resetEngineBreaker(p.fetcher, p.engine);
      await load();
      onChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not turn it back on");
    } finally {
      setBusy(null);
    }
  };

  const paused = pausedPairs(matrix);
  if (!matrix) return null;
  return (
    <div className="mt-4" data-testid="paused-engines">
      <div className="mb-1 block text-xs text-gray-600 dark:text-gray-300">
        Paused after repeated failures
      </div>
      {paused.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          None - every engine is being tried.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
          {paused.map((p) => {
            const key = `${p.fetcher}:${p.engine}`;
            return (
              <li key={key} className="flex items-start gap-3 p-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-gray-800 dark:text-gray-100">
                    {p.engine} on {FETCHER_LABELS[p.fetcher] ?? p.fetcher}
                    <span className="ml-2 text-xs text-amber-700 dark:text-amber-300">
                      skipped for {timeLeft(p.secondsLeft)}
                    </span>
                  </div>
                  {p.why.length > 0 && (
                    <ul className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                      {p.why.map((w, i) => (
                        <li key={i}>
                          {w.query ? `“${w.query}”: ` : ""}
                          {w.reason || "failed"}
                          {w.looks ? ` (${w.looks})` : ""}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => void turnOn(p)}
                  className="shrink-0 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  {busy === key ? "Turning on…" : "Turn back on"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {error && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
